/**
 * Pagina super_admin: strumenti di manutenzione dati.
 * Per ora un solo strumento: Dedup categorie ticket + riassegnazione orfani.
 * Future: backup DB, export, vacuum, ecc.
 */
import React, { useState } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Wrench, CheckCircle2, Database, RefreshCw, Download, Upload, Archive, AlertTriangle, Snowflake, History, PlayCircle } from 'lucide-react';

const AdminToolsPage = () => {
  const { user } = useAuth();
  // Sync standard categories
  const [syncPlan, setSyncPlan] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Backup / Restore state
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dropExisting, setDropExisting] = useState(false);
  // Archive rolling window state
  const [archiveStats, setArchiveStats] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveRunning, setArchiveRunning] = useState(false);
  const [archiveThreshold, setArchiveThreshold] = useState(60);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center text-slate-500">
            Accesso riservato ai Super Admin.
          </CardContent>
        </Card>
      </div>
    );
  }

  const runSync = async (apply) => {
    try {
      setSyncing(true);
      const { data } = await api.post(`/categories/sync-standard?apply=${apply}`);
      setSyncPlan(data);
      toast.success(apply
        ? `Aggiunte ${data.inserted?.length || 0} categorie. Totale ora: ${data.total_after}.`
        : `Simulazione: ${data.missing.length} categorie da aggiungere.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore sync categorie');
    } finally {
      setSyncing(false);
    }
  };

  const loadArchiveStats = async (thresholdDays = archiveThreshold) => {
    try {
      setArchiveLoading(true);
      const { data } = await api.get(`/admin/archive/stats?threshold_days=${thresholdDays}`);
      setArchiveStats(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento stats archivio');
    } finally {
      setArchiveLoading(false);
    }
  };

  const runArchive = async () => {
    const preview = archiveStats?.would_archive_now || 0;
    if (preview === 0) {
      toast.info('Nessun ticket da archiviare.');
      return;
    }
    if (!window.confirm(`Confermi l'archiviazione di ${preview} ticket chiusi da oltre ${archiveThreshold} giorni?\n\nSaranno esclusi dalle liste di default ma restano consultabili con "Mostra archivio".`)) {
      return;
    }
    try {
      setArchiveRunning(true);
      const { data } = await api.post('/admin/archive/run', { threshold_days: archiveThreshold });
      toast.success(`Archiviati ${data.archived} ticket.`);
      await loadArchiveStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore archiviazione');
    } finally {
      setArchiveRunning(false);
    }
  };

  const restoreAllArchive = async () => {
    const count = archiveStats?.archived_tickets || 0;
    if (count === 0) {
      toast.info('Nessun ticket in archivio.');
      return;
    }
    if (!window.confirm(`Ripristinare TUTTI i ${count} ticket in archivio?\n\nTorneranno visibili nelle liste di default.`)) {
      return;
    }
    try {
      setArchiveRunning(true);
      const { data } = await api.post('/admin/archive/restore', { restore_all: true });
      toast.success(`Ripristinati ${data.restored} ticket.`);
      await loadArchiveStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore ripristino');
    } finally {
      setArchiveRunning(false);
    }
  };

  const loadManifest = async () => {
    try {
      setManifestLoading(true);
      const { data } = await api.get('/admin/db-export/manifest');
      setManifest(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore lettura manifest');
    } finally {
      setManifestLoading(false);
    }
  };

  const downloadBackup = async () => {
    try {
      setDownloading(true);
      const response = await api.get('/admin/db-export', {
        responseType: 'blob',
        timeout: 600000, // 10 min per dump grandi
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `hostfix_backup_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup scaricato con successo');
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Timeout: il dump e troppo grosso. Prova le collection singole qui sotto.'
        : (e.response?.data?.detail || 'Errore durante il download del backup');
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const downloadSingleCollection = async (name) => {
    try {
      const response = await api.get(`/admin/db-export/collection?name=${name}`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const blob = new Blob([response.data], { type: 'application/x-ndjson' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `hostfix_${name}_${stamp}.jsonl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Collection "${name}" scaricata`);
    } catch (e) {
      toast.error(`Errore download ${name}`);
    }
  };

  const uploadBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      toast.error('Carica un file .zip');
      return;
    }
    const confirmMsg = dropExisting
      ? `ATTENZIONE: DROP+INSERT su tutte le collection. TUTTI I DATI ATTUALI VERRANNO CANCELLATI. Continuare?`
      : `Verranno importati/aggiornati i documenti dal file "${file.name}" (modalita upsert, dati attuali preservati). Continuare?`;
    if (!window.confirm(confirmMsg)) {
      event.target.value = '';
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/admin/db-import?drop_existing=${dropExisting}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min timeout for large restores
      });
      setUploadResult(data);
      toast.success('Import completato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante import');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6" data-testid="admin-tools-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Wrench className="w-7 h-7 text-amber-600" /> Strumenti Dati
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Operazioni di manutenzione DB riservate ai Super Admin.
        </p>
      </div>

      <Card className="border-emerald-200 dark:border-emerald-900 dark:bg-slate-900">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-600" />
            Aggiungi nuove categorie ticket
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
            <p><b>Cosa fa</b>: aggiunge al sistema 4 nuove categorie operative:</p>
            <ul className="list-disc list-inside ml-2 text-slate-600 dark:text-slate-400">
              <li><code>Dispositivi camera</code> (minibar, cassaforte, telefoni)</li>
              <li><code>Siliconature</code></li>
              <li><code>Pitture</code></li>
              <li><code>Serramenti</code></li>
            </ul>
            <p className="pt-1 text-emerald-700 dark:text-emerald-300">
              <b>Sicuro:</b> non tocca, non rinomina, non elimina <b>nessuna</b> categoria esistente.
              Se una categoria è già presente, viene saltata. Lancialo quante volte vuoi.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => runSync(false)} disabled={syncing} variant="outline" data-testid="sync-dryrun-button">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Verifica cosa manca
            </Button>
            <Button
              onClick={() => runSync(true)}
              disabled={syncing || !syncPlan || syncPlan.missing?.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="sync-apply-button"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Crea categorie mancanti
            </Button>
          </div>
          {syncPlan && (
            <div className="mt-3 border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40 dark:border-slate-700 text-sm" data-testid="sync-plan-result">
              <p>
                <Badge className={syncPlan.dry_run ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                  {syncPlan.dry_run ? 'Simulazione' : 'Eseguito'}
                </Badge>
                <span className="ml-2">
                  Categorie già presenti: <b>{syncPlan.present?.length || 0}</b> · Mancanti: <b>{syncPlan.missing?.length || 0}</b>
                  {syncPlan.total_after !== undefined && <> · Totale ora: <b>{syncPlan.total_after}</b></>}
                </span>
              </p>
              {syncPlan.missing?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {syncPlan.missing.map((c, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{c.name}</Badge>
                  ))}
                </div>
              )}
              {syncPlan.inserted?.length > 0 && (
                <p className="mt-2 text-green-700 dark:text-green-300">
                  ✓ Aggiunte: {syncPlan.inserted.map(i => i.name).join(', ')}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archivio Rolling 60gg (Sprint 2 Scalability) */}
      <Card className="border-sky-200 dark:border-sky-900 dark:bg-slate-900" data-testid="archive-rolling-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Snowflake className="w-5 h-5 text-sky-600" />
            Archivio Rolling — Ticket &gt; 60 giorni
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Mantiene attivi solo i ticket recenti. Quelli chiusi/completati oltre la soglia vengono archiviati
            (esclusi dalle liste di default) ma restano sempre consultabili.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
            <p><b>Perché serve</b>: a 12.000+ ticket, ridurre il dataset attivo velocizza dashboard, liste e analytics del 60-80%.</p>
            <p><b>Cosa fa</b>: marca <code>is_archived=true</code> su ticket <b>chiusi/completati</b> con <code>closed_at</code> più vecchio della soglia.</p>
            <p className="text-sky-700 dark:text-sky-300">
              <b>Automatico:</b> il job gira <b>ogni 24h</b> in background con soglia 60gg. Nessun dato viene mai eliminato.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Soglia (giorni)</label>
              <input
                type="number"
                min={7}
                max={3650}
                value={archiveThreshold}
                onChange={(e) => setArchiveThreshold(parseInt(e.target.value || '60', 10))}
                className="w-24 px-3 py-1.5 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-700 text-sm"
                data-testid="archive-threshold-input"
              />
            </div>
            <Button
              onClick={() => loadArchiveStats(archiveThreshold)}
              variant="outline"
              disabled={archiveLoading}
              data-testid="archive-preview-button"
            >
              {archiveLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Anteprima
            </Button>
            <Button
              onClick={runArchive}
              disabled={!archiveStats || archiveRunning || (archiveStats?.would_archive_now || 0) === 0}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="archive-run-button"
            >
              {archiveRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              Archivia ora
            </Button>
            <Button
              onClick={restoreAllArchive}
              disabled={!archiveStats || archiveRunning || (archiveStats?.archived_tickets || 0) === 0}
              variant="outline"
              className="text-amber-700 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
              data-testid="archive-restore-all-button"
            >
              <History className="w-4 h-4 mr-2" />
              Ripristina tutto
            </Button>
          </div>

          {archiveStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="archive-stats-grid">
              <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40 dark:border-slate-700">
                <div className="text-xs text-slate-500">Ticket totali</div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="archive-total">
                  {archiveStats.total_tickets}
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900">
                <div className="text-xs text-emerald-700 dark:text-emerald-300">Attivi (dataset caldo)</div>
                <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300" data-testid="archive-active">
                  {archiveStats.active_tickets}
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-sky-50 dark:bg-sky-950/20 dark:border-sky-900">
                <div className="text-xs text-sky-700 dark:text-sky-300">In archivio</div>
                <div className="text-2xl font-bold text-sky-700 dark:text-sky-300" data-testid="archive-archived">
                  {archiveStats.archived_tickets}
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900">
                <div className="text-xs text-amber-700 dark:text-amber-300">Da archiviare ora</div>
                <div className="text-2xl font-bold text-amber-700 dark:text-amber-300" data-testid="archive-would-archive">
                  {archiveStats.would_archive_now}
                </div>
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500 dark:text-slate-400 border-t pt-3 dark:border-slate-700">
            <b>Come consultare l&apos;archivio:</b> vai su <i>Tutti i Ticket</i> e attiva il toggle <b>&quot;Mostra archivio&quot;</b>. Analytics e Data Intel su periodi lunghi (&gt; 60gg) leggono automaticamente anche gli archiviati.
          </div>
        </CardContent>
      </Card>

      {/* Backup / Restore DB */}
      <Card data-testid="db-backup-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Archive className="w-5 h-5 text-purple-600" />
            Backup &amp; Restore Database
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Esporta l&apos;intero database in un file ZIP (JSON per collection). Utile per backup periodici, migrazione tra ambienti, o rollback.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="p-3 rounded-md flex items-start gap-2 text-xs"
            style={{ background: 'rgba(245, 158, 11, 0.10)', border: '1px solid #fbbf24', color: '#92400e' }}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>Attenzione:</b> il backup contiene <b>dati personali</b> e <b>hash password</b>. Custodisci il file come una password.
              Le foto GridFS <b>non</b> sono incluse (vengono comunque auto-eliminate alla chiusura ticket).
            </div>
          </div>

          {/* Export */}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={loadManifest}
                disabled={manifestLoading}
                variant="outline"
                data-testid="load-manifest-button"
              >
                {manifestLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Analizza dimensione
              </Button>
              <Button
                onClick={downloadBackup}
                disabled={downloading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="download-backup-button"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Scarica backup ZIP
              </Button>
            </div>

            {manifest && (
              <div className="mt-2 border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40 text-xs" data-testid="manifest-result">
                <div className="flex items-center justify-between mb-2">
                  <b>Totale documenti:</b>
                  <Badge variant="secondary">{manifest.total_documents}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {Object.entries(manifest.collections).map(([k, v]) => (
                    <div key={k} className="flex justify-between items-center border-b border-slate-200/50 py-0.5 gap-2">
                      <span className="text-slate-600 dark:text-slate-400 truncate">{k}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">{typeof v === 'number' ? v : '⚠'}</span>
                        {typeof v === 'number' && v > 0 && (
                          <button
                            onClick={() => downloadSingleCollection(k)}
                            className="text-purple-600 hover:text-purple-800 text-[10px] underline"
                            data-testid={`dl-${k}`}
                            title={`Scarica solo ${k}`}
                          >
                            .jsonl
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-slate-500">
                  Stima ZIP: ~{manifest.estimated_zip_mb_min} MB (dopo compressione). Se il download completo fallisce (dump &gt; 100 MB o &gt; 200k documenti), scarica <b>una collection alla volta</b> cliccando su <code className="text-purple-600">.jsonl</code> nella riga corrispondente.
                </p>
              </div>
            )}
          </div>

          {/* Import */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-slate-500" />
              <b className="text-sm">Ripristina da backup</b>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                id="drop-existing"
                checked={dropExisting}
                onChange={(e) => setDropExisting(e.target.checked)}
                className="rounded border-slate-300"
                data-testid="drop-existing-checkbox"
              />
              <label htmlFor="drop-existing" className="text-slate-700 dark:text-slate-300 cursor-pointer">
                <b className="text-red-600 dark:text-red-400">Drop &amp; insert</b> — cancella tutto e sostituisce (SOLO in preview!)
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Default: <b>upsert per _id</b>. I documenti esistenti con lo stesso _id vengono aggiornati; i nuovi vengono inseriti; il resto rimane.
            </p>
            <label className="inline-block">
              <input
                type="file"
                accept=".zip"
                onChange={uploadBackup}
                disabled={uploading}
                className="hidden"
                data-testid="upload-backup-input"
              />
              <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-md text-white text-sm font-semibold cursor-pointer ${uploading ? 'bg-slate-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Import in corso...' : 'Carica backup ZIP'}
              </span>
            </label>

            {uploadResult && (
              <div className="mt-2 border rounded-lg p-3 bg-emerald-50 dark:bg-emerald-950/30 text-xs" data-testid="import-result">
                <div className="flex items-center gap-2 mb-2 text-emerald-700 dark:text-emerald-300 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Import completato
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                  {Object.entries(uploadResult.stats || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-emerald-200/40 py-0.5">
                      <span className="text-slate-600 dark:text-slate-400 truncate">{k}</span>
                      <span className="font-mono font-semibold text-emerald-700 dark:text-emerald-300 shrink-0 ml-2">
                        {typeof v === 'object' ? `+${v.inserted || 0}` + (v.updated ? ` ~${v.updated}` : '') : String(v).slice(0, 12)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ================================ BONIFICA NOTIFICHE ================================ */}
      <NotificationsCleanupCard />

      {/* ================================ LOG NOTIFICHE INVIATE ================================ */}
      <Card data-testid="notification-logs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-500" />
            Log Notifiche Inviate
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Ultime 100 notifiche (push + email) con destinatario, ticket ed esito.
            Fondamentale per il debug (retention 30 giorni, auto-purge).
          </p>
          <Button
            asChild
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="open-notification-logs-btn"
          >
            <a href="/log-notifiche">Apri Log Notifiche</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- Bonifica notifiche (push scadute + email seed non deliverabili + denylist bounce) ----------
const NotificationsCleanupCard = () => {
  const [preview, setPreview] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [skipProbe, setSkipProbe] = React.useState(true);
  const [bounced, setBounced] = React.useState([]);
  const [bouncedLoading, setBouncedLoading] = React.useState(false);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/notifications/cleanup/preview?skip_probe=${skipProbe}`);
      setPreview(data);
      toast.success(`Anteprima: ${data.push_structurally_bad.length + data.push_dead_after_probe.length} push da eliminare, ${data.seed_users_to_disable.length} email da disabilitare`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview bonifica');
    } finally {
      setLoading(false);
    }
  };

  const runCleanup = async () => {
    try {
      // Se non c'è un'anteprima recente, la calcolo ora così il confirm mostra i numeri veri
      let p = preview;
      if (!p) {
        setLoading(true);
        try {
          const { data } = await api.get(`/admin/notifications/cleanup/preview?skip_probe=${skipProbe}`);
          setPreview(data);
          p = data;
        } finally {
          setLoading(false);
        }
      }
      const nPush = (p?.push_structurally_bad?.length || 0) + (p?.push_dead_after_probe?.length || 0);
      const nUsers = p?.seed_users_to_disable?.length || 0;

      if (nPush === 0 && nUsers === 0) {
        toast.info('Nulla da bonificare, il DB è già pulito 🎉');
        return;
      }

      const msg =
        `Confermi la bonifica?\n\n` +
        `• ${nPush} push subscription verranno ELIMINATE` +
        (p.push_structurally_bad.length ? ` (${p.push_structurally_bad.length} malformate` : '') +
        (p.push_dead_after_probe.length ? `${p.push_structurally_bad.length ? ', ' : ' ('}${p.push_dead_after_probe.length} scadute` : '') +
        (p.push_structurally_bad.length || p.push_dead_after_probe.length ? ')' : '') + `\n` +
        `• ${nUsers} utenti verranno disabilitati (notify_email=false)` +
        (nUsers > 0 ? `\n  ${p.seed_users_to_disable.slice(0, 3).map((x) => x.email).join(', ')}${nUsers > 3 ? ` … +${nUsers - 3}` : ''}` : '') +
        `\n\nL'operazione è idempotente e loggata in audit.`;

      if (!window.confirm(msg)) return;

      setRunning(true);
      const { data } = await api.post(`/admin/notifications/cleanup/run?skip_probe=${skipProbe}`);
      toast.success(`Bonifica completata: ${data.deleted_push_tokens} push eliminati, ${data.disabled_users_notify_email} utenti disabilitati`);
      setPreview(null);
      loadBounced();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore bonifica');
    } finally {
      setRunning(false);
    }
  };

  const loadBounced = async () => {
    try {
      setBouncedLoading(true);
      const { data } = await api.get('/admin/notifications/bounced');
      setBounced(data.items || []);
    } catch (e) {
      // silenzioso
    } finally {
      setBouncedLoading(false);
    }
  };

  const removeBounced = async (email) => {
    if (!window.confirm(`Rimuovere ${email} dalla denylist?`)) return;
    try {
      await api.delete(`/admin/notifications/bounced/${encodeURIComponent(email)}`);
      toast.success(`${email} rimosso dalla denylist`);
      loadBounced();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rimozione');
    }
  };

  React.useEffect(() => { loadBounced(); }, []);

  return (
    <Card data-testid="notifications-cleanup-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-cyan-600" />
          Bonifica Notifiche
          <Badge variant="outline" className="ml-2 text-xs">Push + Email</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">
          Elimina push subscription scadute/malformate (browser disinstallato, chiavi corrotte) e disabilita le email seed non deliverabili (es. <code>tecnico1@investhosp.com</code>).
          Riduce il rumore nei log e velocizza gli invii massivi.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={skipProbe}
            onChange={(e) => setSkipProbe(e.target.checked)}
            data-testid="cleanup-skip-probe"
          />
          <span>Salta ping VAPID (più veloce, controlla solo la struttura dei token)</span>
        </label>

        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadPreview} disabled={loading || running} data-testid="cleanup-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Anteprima (dry-run)
          </Button>
          <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={runCleanup} disabled={running || loading} data-testid="cleanup-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
            Esegui bonifica
          </Button>
        </div>

        {preview && (
          <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-900/40 text-xs space-y-3" data-testid="cleanup-preview-result">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Anteprima (nessuna modifica applicata)
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2 text-center">
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{preview.push_tokens_total}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Push totali</div>
              </div>
              <div className="rounded bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-2 text-center">
                <div className="text-2xl font-bold text-red-700 dark:text-red-300">{preview.push_structurally_bad.length + preview.push_dead_after_probe.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-red-600">Da eliminare</div>
              </div>
              <div className="rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2 text-center">
                <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{preview.seed_users_to_disable.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-amber-600">Email da disabilitare</div>
              </div>
            </div>
            {[
              ['Push malformati', preview.push_structurally_bad],
              ['Push scaduti (probe)', preview.push_dead_after_probe],
              ['Utenti da disabilitare', preview.seed_users_to_disable],
            ].map(([label, list]) => (
              list.length > 0 && (
                <details key={label} className="text-slate-600 dark:text-slate-400">
                  <summary className="cursor-pointer font-semibold py-1">{label} ({list.length})</summary>
                  <ul className="pl-4 space-y-0.5 mt-1">
                    {list.map((x, i) => (
                      <li key={i} className="font-mono">{x.email || '???'} <span className="text-slate-400">— {x.reason || ''}</span></li>
                    ))}
                  </ul>
                </details>
              )
            ))}
          </div>
        )}

        {/* Denylist bounce */}
        <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Denylist Bounce Email</div>
            <Button variant="ghost" size="sm" onClick={loadBounced} disabled={bouncedLoading} data-testid="bounced-refresh-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${bouncedLoading ? 'animate-spin' : ''}`} /> Aggiorna
            </Button>
          </div>
          {bounced.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Nessun indirizzo in denylist. 🎉</p>
          ) : (
            <ul className="space-y-1 max-h-52 overflow-auto" data-testid="bounced-list">
              {bounced.map((b) => (
                <li key={b.email} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-red-50 dark:bg-red-950/30 border border-red-200/50 dark:border-red-800/50">
                  <span className="font-mono truncate mr-2">{b.email}</span>
                  <span className="text-slate-500 truncate flex-1">{b.reason}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-700 hover:bg-red-100" onClick={() => removeBounced(b.email)} data-testid={`bounced-remove-${b.email}`}>
                    Rimuovi
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminToolsPage;
